import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { AgentKey, MintedKey } from '../api/types';

/**
 * Generates a copy-paste MCP server entry (the `mcpServers` shape used by Claude
 * Desktop, Cursor, Hermes, …) for the project.dikonek.com MCP server.
 *
 * You pick a key from the dropdown and everything else fills in. The raw key is
 * only known at mint time (we store a hash), so it auto-fills for the key you
 * just generated; for any older key you paste the value you saved — there's no
 * way for us to recover it.
 */

// Default install path of the built MCP server entry (edit per machine if needed).
const DEFAULT_DIST =
  'C:/Users/Haikal/source/dikonek/dikonek/apps/project/mcp-server/dist/index.js';
const DEFAULT_BASE_URL = 'http://127.0.0.1:8090/api/agent';
const KEY_PLACEHOLDER = 'dkop_PASTE_THE_KEY_YOU_SAVED';

type Transport = 'stdio' | 'dev' | 'http';
/** 'entry' = bare command/args/env (Hermes); 'wrapped' = mcpServers map (Claude Desktop, Cursor). */
type ConfigShape = 'entry' | 'wrapped';

function srcFromDist(dist: string): string {
  return dist.replace(/dist[/\\]index\.js$/, 'src/index.ts');
}

function buildConfig(opts: {
  transport: Transport;
  shape: ConfigShape;
  distPath: string;
  baseUrl: string;
  key: string;
  agentId: string;
}): string {
  const env: Record<string, string> = {
    PROJECT_AGENT_BASE_URL: opts.baseUrl,
    PROJECT_AGENT_API_KEY: opts.key || KEY_PLACEHOLDER,
    PROJECT_AGENT_ID: opts.agentId || 'dikonek-project-agent',
  };
  // stdio is the server's default; only spell out the transport for http.
  if (opts.transport === 'http') {
    env.MCP_TRANSPORT = 'http';
    env.MCP_HTTP_PORT = '8009';
  }

  const entry =
    opts.transport === 'dev'
      ? { command: 'npx', args: ['tsx', srcFromDist(opts.distPath)], env }
      : { command: 'node', args: [opts.distPath], env };

  const payload =
    opts.shape === 'entry'
      ? { ...entry, enabled: true } // Hermes per-server entry carries an enabled flag
      : { mcpServers: { 'dikonek-project': entry } };
  return JSON.stringify(payload, null, 2);
}

export function ConnectAgentCard({
  keys,
  minted,
}: {
  /** All keys (the selectable ones are those still usable). */
  keys: AgentKey[];
  /** The key just generated this session — the only one whose raw value we hold. */
  minted: MintedKey | null;
}) {
  const usable = keys.filter((k) => !k.revokedAt && k.enabled);
  const defaultId = minted?.id ?? usable[0]?.id ?? '';

  const [selectedId, setSelectedId] = useState(defaultId);
  const [shape, setShape] = useState<ConfigShape>('entry');
  const [transport, setTransport] = useState<Transport>('stdio');
  const [distPath, setDistPath] = useState(DEFAULT_DIST);
  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE_URL);
  const [pastedKey, setPastedKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  const selectedKey = keys.find((k) => k.id === selectedId);
  // We only hold the raw value for the key minted this session.
  const haveRawKey = minted != null && selectedId === minted.id;
  const rawKey = haveRawKey ? minted.key : pastedKey;
  const agentId = selectedKey?.name ?? minted?.name ?? '';

  const config = useMemo(
    () => buildConfig({ transport, shape, distPath, baseUrl, key: rawKey, agentId }),
    [transport, shape, distPath, baseUrl, rawKey, agentId],
  );

  const copy = async () => {
    await navigator.clipboard.writeText(config);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const copyKey = async () => {
    if (!rawKey) return;
    await navigator.clipboard.writeText(rawKey);
    setCopiedKey(true);
    window.setTimeout(() => setCopiedKey(false), 2000);
  };

  if (usable.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Connect an agent
        </Typography>
        <Alert severity="info">Generate a key first — then a ready-to-paste config appears here.</Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Connect an agent
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick the key the agent should use; the config fills in. Paste it into the agent's MCP settings
        (Claude Desktop, Cursor, Hermes…). The backend must be running and the key active.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Key"
          value={selectedId}
          onChange={(e) => {
            setSelectedId(e.target.value);
            setPastedKey('');
          }}
          sx={{ minWidth: 280 }}
        >
          {usable.map((k) => (
            <MenuItem key={k.id} value={k.id}>
              {k.name} ({k.keyPrefix}…){minted && k.id === minted.id ? ' — just generated' : ''}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Format"
          value={shape}
          onChange={(e) => setShape(e.target.value as ConfigShape)}
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="entry">Hermes — entry only (command/args/env)</MenuItem>
          <MenuItem value="wrapped">Claude Desktop / Cursor — mcpServers map</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Transport"
          value={transport}
          onChange={(e) => setTransport(e.target.value as Transport)}
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="stdio">stdio — agent spawns built server (node dist)</MenuItem>
          <MenuItem value="dev">dev — run TypeScript directly (npx tsx)</MenuItem>
          <MenuItem value="http">http — long-lived server on :8009</MenuItem>
        </TextField>
      </Stack>

      {haveRawKey ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Using the key you just generated — its secret is embedded below. This is the only time we
          can fill it for you, so copy the config now.
        </Alert>
      ) : (
        <TextField
          size="small"
          label={`Secret for "${selectedKey?.name ?? ''}"`}
          value={pastedKey}
          onChange={(e) => setPastedKey(e.target.value)}
          placeholder={KEY_PLACEHOLDER}
          fullWidth
          sx={{ mb: 2 }}
          helperText="We store only a hash, so we can't show this key's secret. Paste the value you saved when you generated it — or generate a new key to get a fresh one."
        />
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="MCP server entry path"
          value={distPath}
          onChange={(e) => setDistPath(e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label="Backend agent base URL"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          fullWidth
        />
      </Stack>

      {transport === 'http' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          HTTP transport: start the server yourself (<code>npm start</code> in the mcp-server dir with
          these env vars) and point the agent at <code>http://127.0.0.1:8009/mcp</code> instead of the
          command/args below.
        </Alert>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle2">MCP config — paste into the agent</Typography>
          {!rawKey && <Chip size="small" color="warning" variant="outlined" label="needs the key" />}
        </Stack>
        <Stack direction="row" gap={1}>
          {rawKey && (
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={copyKey}
              variant="outlined"
              color={copiedKey ? 'success' : 'primary'}
            >
              {copiedKey ? 'Copied!' : 'Copy key'}
            </Button>
          )}
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={copy}
            variant="contained"
            color={copied ? 'success' : 'primary'}
          >
            {copied ? 'Copied!' : 'Copy config'}
          </Button>
        </Stack>
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          bgcolor: 'grey.900',
          color: 'grey.100',
          borderRadius: 1,
          overflowX: 'auto',
          fontSize: 13,
          fontFamily: 'monospace',
        }}
      >
        {config}
      </Box>
    </Paper>
  );
}
